import { Loader } from "components";
import { APP_ROUTES } from "const";
import { useMe } from "hooks";
import classnames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { USER_ROLES } from "types";
import {
  ContentModerationSection,
  OverviewSection,
  UserManagementSection,
  VisualThemeSection,
} from "./components";

type SectionId = "overview" | "content" | "users" | "visual";

export function Admin() {
  const { data: me, isLoading } = useMe();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<SectionId>("overview");

  const roles = me?.data?.roles ?? [];
  const isAdmin = roles.includes(USER_ROLES.ADMIN);

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      const canGoBack =
        typeof window !== "undefined" && window.history && window.history.length > 1;

      if (canGoBack) {
        navigate(-1);
      } else {
        navigate(APP_ROUTES.DASHBOARD, { replace: true });
      }
    }
  }, [isLoading, isAdmin, navigate]);

  const sections = useMemo(
    () =>
      [
        {
          id: "overview" as SectionId,
          label: "Overview",
          helper: "Start here",
          component: <OverviewSection />,
        },
        {
          id: "content" as SectionId,
          label: "Content Moderation",
          helper: "Plan deletion workflow",
          component: <ContentModerationSection />,
        },
        {
          id: "users" as SectionId,
          label: "User Management",
          helper: "Manage members",
          component: <UserManagementSection />,
        },
        {
          id: "visual" as SectionId,
          label: "Visual Theme",
          helper: "Backgrounds & colour",
          component: <VisualThemeSection />,
        },
      ],
    []
  );

  const active = sections.find((section) => section.id === activeSection) ?? sections[0];

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-12">
        <Loader type="rings" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
        <p className="text-sm text-gray-600">
          Manage community content, membership, and the look-and-feel of the platform from this
          workspace.
        </p>
      </header>
      <div className="flex flex-col gap-6 lg:flex-row">
        <nav className="flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:w-64 lg:flex-shrink-0">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSection(section.id)}
              className={classnames(
                "rounded-md px-4 py-3 text-left text-sm font-semibold transition",
                {
                  "bg-indigo-600 text-white shadow": activeSection === section.id,
                  "bg-gray-50 text-gray-700 hover:bg-gray-100": activeSection !== section.id,
                }
              )}
            >
              <span className="block">{section.label}</span>
              <span className="block text-xs font-normal opacity-80">{section.helper}</span>
            </button>
          ))}
        </nav>
        <main className="flex-1">
          {active?.component}
        </main>
      </div>
    </div>
  );
}
